/**
 * ConfirmPH: Admin support tickets inbox (platform_admin only).
 *
 * DEPRECATED (Support V1 closure audit, 2026-08-16): kept read-only for
 * ticket history + status tidying. New/ongoing support work happens in the
 * Support Workspace (/admin/support-agents to manage agents, the workspace
 * itself at /support-workspace for agents). The backend rejects new replies
 * here with a 410.
 */
import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Headset, Loader2, ChevronLeft,
} from "lucide-react";

const CATEGORY_LABEL = {
  billing: "Billing", bug: "Bug", feature_request: "Feature request",
  account: "Account", booking_issue: "Booking issue",
  technical_problem: "Technical problem", abuse_report: "Abuse / report",
  other: "Other",
};

const PRIORITY_LABEL = { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" };

const STATUS_TONE = {
  open:                "bg-amber-100 text-amber-800 border-amber-200",
  waiting_for_support: "bg-amber-100 text-amber-800 border-amber-200",
  waiting_for_business:"bg-indigo-100 text-indigo-800 border-indigo-200",
  resolved:            "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed:              "bg-slate-200 text-slate-700 border-slate-300",
};

const STATUS_OPTIONS = ["open", "waiting_for_support", "waiting_for_business",
                         "resolved", "closed"];

export default function AdminSupportTickets() {
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "active", category: "", priority: "" });
  const [openTicketId, setOpenTicketId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.priority) params.priority = filters.priority;
      const { data } = await api.get("/admin/support/tickets", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  if (openTicketId) {
    return <AdminTicketDetail id={openTicketId} onBack={() => { setOpenTicketId(null); load(); }} />;
  }

  return (
    <div className="space-y-5" data-testid="admin-support-tickets-page">
      <header>
        <h1 className="font-display text-2xl font-semibold inline-flex items-center gap-2">
          <Headset className="w-5 h-5" /> Support tickets
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Every business support ticket on ConfirmPH. Read-only history: status can still be changed here,
          but replies now go through the Support Workspace.
        </p>
      </header>

      <div className="flex flex-wrap gap-2 items-center" data-testid="admin-support-filters">
        <select value={filters.status}
                onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                className="text-xs rounded-lg border border-slate-300 px-2 py-1.5"
                data-testid="admin-support-filter-status">
          <option value="active">Active</option>
          <option value="">All</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select value={filters.category}
                onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
                className="text-xs rounded-lg border border-slate-300 px-2 py-1.5"
                data-testid="admin-support-filter-category">
          <option value="">All categories</option>
          {Object.entries(CATEGORY_LABEL).map(([v, l]) =>
            <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.priority}
                onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}
                className="text-xs rounded-lg border border-slate-300 px-2 py-1.5"
                data-testid="admin-support-filter-priority">
          <option value="">All priorities</option>
          {Object.entries(PRIORITY_LABEL).map(([v, l]) =>
            <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-500">
          <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
        </div>
      ) : data.items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-10 text-center text-sm text-slate-600"
             data-testid="admin-support-empty">
          No tickets match these filters.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="admin-support-list">
          {data.items.map(t => (
            <li key={t.id}>
              <button onClick={() => setOpenTicketId(t.id)}
                      className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-slate-300"
                      data-testid={`admin-support-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{t.subject}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {t.company_name || t.company_id?.slice(0, 8)}
                      <span className="mx-1.5">·</span>
                      {CATEGORY_LABEL[t.category] || t.category}
                      <span className="mx-1.5">·</span>
                      Priority: {PRIORITY_LABEL[t.priority] || t.priority}
                      <span className="mx-1.5">·</span>
                      Updated {t.updated_at?.slice(0, 10)}
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_TONE[t.status] || ""}`}>
                    {t.status.replace(/_/g, " ")}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AdminTicketDetail({ id, onBack }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/support/tickets/${id}`);
      setTicket(data);
    } catch (e) {
      toast.error(formatApiError(e));
      onBack();
    } finally {
      setLoading(false);
    }
  }, [id, onBack]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    setChanging(true);
    try {
      const { data } = await api.post(`/admin/support/tickets/${id}/status`,
                                       { status });
      setTicket(data);
      toast.success(`Status updated to ${status.replace(/_/g, " ")}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setChanging(false);
    }
  };

  if (loading || !ticket) {
    return (
      <div className="py-10 text-center text-slate-500">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="admin-support-detail">
      <button onClick={onBack}
              className="text-sm text-slate-600 hover:text-slate-900 underline inline-flex items-center gap-1">
        <ChevronLeft className="w-3 h-3" /> Back to inbox
      </button>

      <header className="space-y-1">
        <h2 className="font-display text-xl font-bold">{ticket.subject}</h2>
        <div className="text-xs text-slate-500">
          <span className="font-semibold text-slate-700">{ticket.company_name || ticket.company_id}</span>
          <span className="mx-1.5">·</span>
          {CATEGORY_LABEL[ticket.category] || ticket.category}
          <span className="mx-1.5">·</span>
          Priority: {PRIORITY_LABEL[ticket.priority] || ticket.priority}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_TONE[ticket.status] || ""}`}>
            {ticket.status.replace(/_/g, " ")}
          </span>
          <select value={ticket.status} disabled={changing}
                  onChange={(e) => setStatus(e.target.value)}
                  className="text-xs rounded-lg border border-slate-300 px-2 py-1"
                  data-testid="admin-support-status-select">
            {STATUS_OPTIONS.map(s =>
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      </header>

      <ul className="space-y-3" data-testid="admin-support-thread">
        {ticket.messages.map(m => (
          <li key={m.id}
              className={`rounded-xl border p-3 ${
                m.sender_role === "support"
                  ? "border-indigo-200 bg-indigo-50/60"
                  : "border-slate-200 bg-white"
              }`}
              data-testid={`admin-message-${m.id}`}>
            <div className="text-[11px] text-slate-500 mb-1">
              <span className="font-semibold text-slate-700">{m.sender_name}</span>
              <span className="ml-1">({m.sender_role === "support" ? "ConfirmPH Support" : "Business"})</span>
              <span className="ml-2">{m.created_at?.replace("T", " ").slice(0, 16)}</span>
            </div>
            <p className="text-sm whitespace-pre-line">{m.text}</p>
          </li>
        ))}
      </ul>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600"
           data-testid="admin-support-reply-disabled-banner">
        This channel no longer accepts new replies: status can still be changed above.
        Direct the business to Help &amp; Support (/app/help) for further contact.
      </div>
    </div>
  );
}
